import { useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { foldForSearch } from '@se-qa/domain';
import { api } from '../lib/api';
import { PageHeader } from '../components/PageHeader';

interface Procedure {
  code: string;
  title: string;
  clauses: readonly string[];
  level: number;
  automation: 'FULL' | 'PARTIAL' | 'TEMPLATE' | 'MANUAL';
  qaModule?: string;
}

interface QmsDocument {
  code: string;
  title: string;
  category: string;
  revision: string;
  status: string;
  path: string;
}

interface DocsResponse {
  total: number;
  grandTotal: number;
  categories: { key: string; label: string }[];
  items: QmsDocument[];
}

const AUTOMATION_LABEL: Record<Procedure['automation'], { label: string; class: string }> = {
  FULL: {
    label: 'Tam Otomatik',
    class: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  },
  PARTIAL: {
    label: 'Kısmi',
    class: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  },
  TEMPLATE: {
    label: 'Şablon',
    class: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200',
  },
  MANUAL: {
    label: 'Manuel',
    class: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  },
};

const STATUS_LABEL: Record<string, { label: string; class: string }> = {
  OK: {
    label: 'Geçerli',
    class: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  },
  STUB: {
    label: 'Taslak yok',
    class: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  },
};

export function RegistryPage() {
  const [tab, setTab] = useState<'documents' | 'procedures'>('documents');

  // --- Tüm dokümanlar (490) ---
  const [docSearch, setDocSearch] = useState('');
  const [category, setCategory] = useState('');
  const docs = useQuery<DocsResponse>({
    queryKey: ['registry', 'documents'],
    queryFn: () => api.get('/v1/registry/documents'),
  });

  const catLabel = useMemo(() => {
    const m = new Map<string, string>();
    docs.data?.categories.forEach((c) => m.set(c.key, c.label));
    return m;
  }, [docs.data]);

  const filteredDocs = useMemo(() => {
    if (!docs.data) return [];
    const s = foldForSearch(docSearch);
    return docs.data.items.filter((d) => {
      if (category && d.category !== category) return false;
      if (s && !foldForSearch(d.code).includes(s) && !foldForSearch(d.title).includes(s)) return false;
      return true;
    });
  }, [docs.data, docSearch, category]);

  // --- Prosedür analizi (46) ---
  const [search, setSearch] = useState('');
  const [automation, setAutomation] = useState<'' | Procedure['automation']>('');
  const [clause, setClause] = useState('');
  const procs = useQuery<{ total: number; items: Procedure[] }>({
    queryKey: ['registry', 'procedures'],
    queryFn: () => api.get('/v1/registry/procedures'),
  });

  const filteredProcs = useMemo(() => {
    if (!procs.data) return [];
    const s = foldForSearch(search);
    return procs.data.items.filter((p) => {
      if (automation && p.automation !== automation) return false;
      if (clause && !p.clauses.includes(clause)) return false;
      if (
        s &&
        !foldForSearch(p.code).includes(s) &&
        !foldForSearch(p.title).includes(s) &&
        !foldForSearch(p.clauses.join(' ')).includes(s)
      ) {
        return false;
      }
      return true;
    });
  }, [procs.data, search, automation, clause]);

  const clausesAll = useMemo(() => {
    if (!procs.data) return [];
    const set = new Set<string>();
    procs.data.items.forEach((p) => p.clauses.forEach((c) => set.add(c)));
    return [...set].sort((a, b) => {
      const [aMajor, aMinor] = a.split('.').map((n) => parseInt(n, 10));
      const [bMajor, bMinor] = b.split('.').map((n) => parseInt(n, 10));
      if ((aMajor ?? 0) !== (bMajor ?? 0)) return (aMajor ?? 0) - (bMajor ?? 0);
      return (aMinor ?? 0) - (bMinor ?? 0);
    });
  }, [procs.data]);

  return (
    <div>
      <PageHeader
        title="Doküman Registry"
        subtitle={`${docs.data?.grandTotal ?? '…'} kontrollü doküman · 46 prosedür AS9100D madde eşlemesi`}
        clause="§7.5 (Doküman Kontrolü)"
      />

      <div className="mb-4 inline-flex rounded-lg border border-slate-200 bg-white p-1 text-sm dark:border-slate-800 dark:bg-slate-900">
        <TabButton active={tab === 'documents'} onClick={() => setTab('documents')}>
          Tüm Dokümanlar {docs.data ? `(${docs.data.grandTotal})` : ''}
        </TabButton>
        <TabButton active={tab === 'procedures'} onClick={() => setTab('procedures')}>
          Prosedür Analizi {procs.data ? `(${procs.data.total})` : ''}
        </TabButton>
      </div>

      {tab === 'documents' ? (
        <>
          <div className="card mb-4 grid gap-3 p-4 sm:grid-cols-[1fr_auto]">
            <input
              type="search"
              className="input"
              placeholder="Kod veya başlık ile ara…"
              value={docSearch}
              onChange={(e) => setDocSearch(e.target.value)}
            />
            <select
              className="input sm:w-56"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              <option value="">Tüm kategoriler</option>
              {docs.data?.categories.map((c) => (
                <option key={c.key} value={c.key}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>

          {docs.isLoading && (
            <div className="card p-6 text-sm text-slate-500 dark:text-slate-400">Yükleniyor…</div>
          )}
          {docs.isError && (
            <div className="card p-6 text-sm text-rose-700 dark:text-rose-300">
              {docs.error instanceof Error ? docs.error.message : 'Hata'}
            </div>
          )}

          {docs.data && (
            <div className="card overflow-hidden">
              <table className="min-w-full divide-y divide-slate-200 text-sm dark:divide-slate-800">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-800/60 dark:text-slate-400">
                  <tr>
                    <th className="px-4 py-3 text-left">Kod</th>
                    <th className="px-4 py-3 text-left">Başlık</th>
                    <th className="px-4 py-3 text-left">Kategori</th>
                    <th className="px-4 py-3 text-left">Rev</th>
                    <th className="px-4 py-3 text-left">Durum</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {filteredDocs.map((d) => {
                    const st = STATUS_LABEL[d.status] ?? {
                      label: d.status,
                      class: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
                    };
                    return (
                      <tr key={d.code} className="hover:bg-slate-50 dark:hover:bg-slate-800/40">
                        <td className="whitespace-nowrap px-4 py-2 font-mono text-xs text-slate-700 dark:text-slate-300">
                          {d.code}
                        </td>
                        <td className="px-4 py-2 text-slate-800 dark:text-slate-200">{d.title}</td>
                        <td className="px-4 py-2 text-xs text-slate-500 dark:text-slate-400">
                          {catLabel.get(d.category) ?? d.category}
                        </td>
                        <td className="px-4 py-2 font-mono text-xs text-slate-500 dark:text-slate-400">
                          {d.revision || '—'}
                        </td>
                        <td className="px-4 py-2">
                          <span className={`badge ${st.class}`}>{st.label}</span>
                        </td>
                      </tr>
                    );
                  })}
                  {filteredDocs.length === 0 && (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-4 py-10 text-center text-sm text-slate-500 dark:text-slate-400"
                      >
                        Eşleşen doküman bulunamadı.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              <div className="border-t border-slate-200 bg-slate-50 px-4 py-2 text-xs text-slate-500 dark:border-slate-800 dark:bg-slate-800/40 dark:text-slate-400">
                {filteredDocs.length} / {docs.data.grandTotal} doküman gösteriliyor
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          <div className="card mb-4 grid gap-3 p-4 sm:grid-cols-[1fr_auto_auto]">
            <input
              type="search"
              className="input"
              placeholder="Kod, başlık veya madde ile ara…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <select
              className="input sm:w-44"
              value={automation}
              onChange={(e) => setAutomation(e.target.value as typeof automation)}
            >
              <option value="">Tüm otomasyon</option>
              <option value="FULL">Tam otomatik</option>
              <option value="PARTIAL">Kısmi</option>
              <option value="TEMPLATE">Şablon</option>
              <option value="MANUAL">Manuel</option>
            </select>
            <select className="input sm:w-44" value={clause} onChange={(e) => setClause(e.target.value)}>
              <option value="">Tüm AS9100 madde</option>
              {clausesAll.map((c) => (
                <option key={c} value={c}>
                  §{c}
                </option>
              ))}
            </select>
          </div>

          {procs.isLoading && (
            <div className="card p-6 text-sm text-slate-500 dark:text-slate-400">Yükleniyor…</div>
          )}
          {procs.data && (
            <div className="card overflow-hidden">
              <table className="min-w-full divide-y divide-slate-200 text-sm dark:divide-slate-800">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-800/60 dark:text-slate-400">
                  <tr>
                    <th className="px-4 py-3 text-left">Kod</th>
                    <th className="px-4 py-3 text-left">Başlık</th>
                    <th className="px-4 py-3 text-left">AS9100D Maddeler</th>
                    <th className="px-4 py-3 text-left">Seviye</th>
                    <th className="px-4 py-3 text-left">Otomasyon</th>
                    <th className="px-4 py-3 text-left">QA Modülü</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {filteredProcs.map((p) => {
                    const a = AUTOMATION_LABEL[p.automation];
                    return (
                      <tr key={p.code} className="hover:bg-slate-50 dark:hover:bg-slate-800/40">
                        <td className="whitespace-nowrap px-4 py-2 font-mono text-xs text-slate-700 dark:text-slate-300">
                          {p.code}
                        </td>
                        <td className="px-4 py-2 text-slate-800 dark:text-slate-200">{p.title}</td>
                        <td className="px-4 py-2 font-mono text-xs text-slate-500 dark:text-slate-400">
                          {p.clauses.map((c) => `§${c}`).join(', ')}
                        </td>
                        <td className="px-4 py-2 text-xs text-slate-500 dark:text-slate-400">L{p.level}</td>
                        <td className="px-4 py-2">
                          <span className={`badge ${a.class}`}>{a.label}</span>
                        </td>
                        <td className="px-4 py-2 text-xs text-slate-500 dark:text-slate-400">
                          {p.qaModule ?? '—'}
                        </td>
                      </tr>
                    );
                  })}
                  {filteredProcs.length === 0 && (
                    <tr>
                      <td
                        colSpan={6}
                        className="px-4 py-10 text-center text-sm text-slate-500 dark:text-slate-400"
                      >
                        Eşleşen prosedür bulunamadı.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              <div className="border-t border-slate-200 bg-slate-50 px-4 py-2 text-xs text-slate-500 dark:border-slate-800 dark:bg-slate-800/40 dark:text-slate-400">
                {filteredProcs.length} / {procs.data.total} prosedür gösteriliyor
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-md px-3 py-1.5 font-medium transition ${
        active
          ? 'bg-brand-600 text-white'
          : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'
      }`}
    >
      {children}
    </button>
  );
}
